import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { buildConsoleRequestHeaders } from '../utils/backendRequest';

const PricingRulesContext = createContext();

function pricingRequestHeaders(token, user) {
  return buildConsoleRequestHeaders(token, user);
}

function deliveryCoreBaseUrl() {
  return process.env.REACT_APP_DELIVERY_CORE_API_URL || '';
}

export function PricingRulesProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [error, setError] = useState(null);
  const refreshInFlightRef = useRef(false);
  const rulesRef = useRef(rules);

  rulesRef.current = rules;

  const refreshRules = useCallback(async ({ environment } = {}) => {
    if (refreshInFlightRef.current) {
      return rulesRef.current;
    }

    refreshInFlightRef.current = true;
    const token = localStorage.getItem('token');
    if (!token) {
      setRules([]);
      setError('pricingRulesMissingToken');
      refreshInFlightRef.current = false;
      return [];
    }

    try {
      setLoading(true);
      setError(null);
      const url = new URL(`${deliveryCoreBaseUrl()}/v1/pricing-rules`, window.location.origin);
      if (environment) {
        url.searchParams.set('environment', environment);
      }

      const res = await fetch(url.toString(), {
        headers: pricingRequestHeaders(token, user),
      });

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setRules(list);
        return list;
      }

      setRules([]);
      setError('pricingRulesLoadFailed');
      return [];
    } catch (err) {
      console.error('Failed to load pricing rules:', err);
      setRules([]);
      setError('pricingRulesLoadFailed');
      return [];
    } finally {
      setLoading(false);
      refreshInFlightRef.current = false;
    }
  }, [user]);

  const createRule = useCallback(async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('pricingRulesMissingToken');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(`${deliveryCoreBaseUrl()}/v1/pricing-rules`, {
        method: 'POST',
        headers: {
          ...pricingRequestHeaders(token, user),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload || {}),
      });

      if (res.ok) {
        return { ok: true, data: await res.json() };
      }

      let detail = '';
      try {
        const body = await res.json();
        detail = body?.detail ? JSON.stringify(body.detail) : '';
      } catch (e) {
        // ignore
      }
      console.error('Failed to create pricing rule:', res.status, detail);
      setError('pricingRulesSaveFailed');
      return { ok: false, status: res.status, detail };
    } catch (err) {
      console.error('Failed to create pricing rule:', err);
      setError('pricingRulesSaveFailed');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [user]);

  const updateRule = useCallback(async (ruleId, payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('pricingRulesMissingToken');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(
        `${deliveryCoreBaseUrl()}/v1/pricing-rules/${encodeURIComponent(ruleId)}`,
        {
          method: 'PATCH',
          headers: {
            ...pricingRequestHeaders(token, user),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload || {}),
        },
      );

      if (res.ok) {
        return { ok: true, data: await res.json() };
      }

      setError('pricingRulesSaveFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to update pricing rule:', err);
      setError('pricingRulesSaveFailed');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [user]);

  const deleteRule = useCallback(async (ruleId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('pricingRulesMissingToken');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(
        `${deliveryCoreBaseUrl()}/v1/pricing-rules/${encodeURIComponent(ruleId)}`,
        {
          method: 'DELETE',
          headers: pricingRequestHeaders(token, user),
        },
      );

      if (res.ok || res.status === 204) {
        return { ok: true };
      }

      setError('pricingRulesDeleteFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to delete pricing rule:', err);
      setError('pricingRulesDeleteFailed');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [user]);

  return (
    <PricingRulesContext.Provider
      value={{
        rules,
        refreshRules,
        createRule,
        updateRule,
        deleteRule,
        loading,
        saving,
        error,
        setError,
      }}
    >
      {children}
    </PricingRulesContext.Provider>
  );
}

export function usePricingRules() {
  return useContext(PricingRulesContext);
}
